import { Fragment, useEffect, useId, useState, type MouseEvent } from "react";
import { useSearchParams } from "react-router";
import ErrorOutlined from "@mui/icons-material/ErrorOutlined";
import FilterCenterFocus from "@mui/icons-material/FilterCenterFocus";
import Info from "@mui/icons-material/Info";
import Layers from "@mui/icons-material/Layers";
import Lock from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { updateParamValue, usePagePrefs } from "@pepsico/pref-permissions";
import { useMap } from "react-map-gl/mapbox";
import { groupBy } from "es-toolkit";

import type { SiteGeoResponse } from "api/model";
import { useGetSitesGeoByLocation } from "api/sites-geo/sites-geo";
import { SELECTED_MAP_ITEM_BGCOLOR } from "components/PlotMap/utils/display";
import {
  PLOT_MAP_LAYER_ORDER,
  PLOT_MAP_LAYERS,
  isPlotMapLayerVisible,
  showPlotMapLayers,
  togglePlotMapLayer,
} from "components/PlotMap/utils/layerPrefs";
import { FIT_BOUNDS_OPTIONS } from "components/PlotMap/utils/mapCameraUtils";
import {
  PLOT_LABEL_MIN_ZOOM,
  ROW_LABEL_MIN_ZOOM,
} from "components/PlotMap/utils/mapConstants";
import {
  isStitchedOrthoStatus,
  useCogOrtho,
  type LocationOrtho,
} from "context/CogOrtho";
import {
  formatOrthoFlightLabel,
  getOrthoFlightDate,
  getOrthoFlightTime,
} from "context/OrthoWorkflow";
import { usePlotMap } from "context/PlotMap";
import { LAYERS_PREF_KEY, type PlotMapLayer } from "defs";

import LayerSwatch from "./LayerSwatch";
import MapControlIconButton from "./MapControlIconButton";

const formatOrthoLayerLabel = (
  ortho: LocationOrtho,
  orthos: readonly LocationOrtho[],
): string => {
  const dateLabel = getOrthoFlightDate(ortho);
  const hasDuplicateDate = orthos.some(
    (other) =>
      other.id !== ortho.id &&
      other.site_id === ortho.site_id &&
      getOrthoFlightDate(other) === dateLabel,
  );
  return formatOrthoFlightLabel(
    dateLabel,
    getOrthoFlightTime(ortho),
    hasDuplicateDate,
  );
};

const getSiteLabel = (siteId: number, siteById: Map<number, SiteGeoResponse>) =>
  siteById.get(siteId)?.site_label?.trim() || `Site #${siteId}`;

interface OrthoSiteGroup {
  siteId: number;
  siteLabel: string;
  orthos: LocationOrtho[];
}

const groupOrthosBySite = (
  orthos: readonly LocationOrtho[],
  siteById: Map<number, SiteGeoResponse>,
): OrthoSiteGroup[] => {
  return Object.values(groupBy(orthos, (ortho) => ortho.site_id))
    .map((siteOrthos) => ({
      siteId: siteOrthos[0].site_id,
      siteLabel: getSiteLabel(siteOrthos[0].site_id, siteById),
      orthos: siteOrthos,
    }))
    .sort((a, b) => a.siteLabel.localeCompare(b.siteLabel));
};

interface LayersControlProps {}

const LayersControl: React.FC<LayersControlProps> = () => {
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [mapZoom, setMapZoom] = useState(0);
  const [prefs, isReady] = usePagePrefs();
  const [, setSearchParams] = useSearchParams();
  const { default: map } = useMap();
  const { orthos, sessions, displayOrtho } = useCogOrtho();
  const {
    boundary: { isGrowerBoundaryMode, isSiteBoundaryMode },
    plot: { isPlotGridMode },
  } = usePlotMap();
  const { data: sites = [] } = useGetSitesGeoByLocation(undefined, {
    query: {
      select: (res): SiteGeoResponse[] => (res.status === 200 ? res.data : []),
    },
  });

  const isOpen = anchorEl != null;
  const siteById = new Map(sites.map((site) => [site.id, site]));
  const mapVisibleOrthos = orthos.filter(
    (ortho) => isStitchedOrthoStatus(ortho.status) && ortho.visible_on_map,
  );
  const orthoSiteGroups = groupOrthosBySite(mapVisibleOrthos, siteById);
  const plotLabelLayerLabel = PLOT_MAP_LAYERS.lbl.label;
  const isPlotLabelVisible = isPlotMapLayerVisible(prefs, "lbl");
  const arePlotLabelsBelowMinZoom = mapZoom < PLOT_LABEL_MIN_ZOOM;
  const rowLabelLayerLabel = PLOT_MAP_LAYERS.rlbl.label;
  const isRowLabelVisible = isPlotMapLayerVisible(prefs, "rlbl");
  const areRowLabelsBelowMinZoom = mapZoom < ROW_LABEL_MIN_ZOOM;
  const nestedLabelLayers: Array<{
    belowMinZoom: boolean;
    label: string;
    layer: PlotMapLayer;
    parent: PlotMapLayer;
    visible: boolean;
    zoomTooltip?: string;
  }> = [
    {
      belowMinZoom: false,
      label: PLOT_MAP_LAYERS.glbl.label,
      layer: "glbl",
      parent: "bnd",
      visible: isPlotMapLayerVisible(prefs, "glbl"),
    },
    {
      belowMinZoom: false,
      label: PLOT_MAP_LAYERS.slbl.label,
      layer: "slbl",
      parent: "site",
      visible: isPlotMapLayerVisible(prefs, "slbl"),
    },
    {
      belowMinZoom: arePlotLabelsBelowMinZoom,
      label: plotLabelLayerLabel,
      layer: "lbl",
      parent: "grid",
      visible: isPlotLabelVisible,
      zoomTooltip: "Zoom in further to show plot labels",
    },
    {
      belowMinZoom: areRowLabelsBelowMinZoom,
      label: rowLabelLayerLabel,
      layer: "rlbl",
      parent: "row",
      visible: isRowLabelVisible,
      zoomTooltip: "Zoom in further to show row labels",
    },
  ];
  const activeEditMode = (
    [
      ["grower", isGrowerBoundaryMode],
      ["plot", isPlotGridMode],
      ["site", isSiteBoundaryMode],
    ] as const
  ).find(([, isActive]) => isActive)?.[0];

  useEffect(() => {
    if (!isReady) {
      setAnchorEl(null);
    }
  }, [isReady]);

  useEffect(() => {
    if (!isReady || activeEditMode == null) {
      return;
    }

    const requiredLayers = (
      Object.keys(PLOT_MAP_LAYERS) as PlotMapLayer[]
    ).filter(
      (layer) => PLOT_MAP_LAYERS[layer].editLock?.mode === activeEditMode,
    );
    if (requiredLayers.every((layer) => isPlotMapLayerVisible(prefs, layer))) {
      return;
    }

    const value = showPlotMapLayers(prefs, requiredLayers).join(",") || null;
    setSearchParams(
      (prev) => updateParamValue(prev, LAYERS_PREF_KEY, prefs, value),
      { replace: true, state: { inPageNav: true } },
    );
  }, [activeEditMode, isReady, prefs, setSearchParams]);

  useEffect(() => {
    const mapboxMap = map?.getMap();
    if (mapboxMap == null) {
      return;
    }

    const handleZoomChange = () => {
      setMapZoom(mapboxMap.getZoom());
    };

    handleZoomChange();
    mapboxMap.on("zoom", handleZoomChange);

    return () => {
      mapboxMap.off("zoom", handleZoomChange);
    };
  }, [map]);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    if (!isReady) {
      return;
    }

    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleLayer = (layer: PlotMapLayer) => {
    if (
      !isReady ||
      (activeEditMode != null &&
        PLOT_MAP_LAYERS[layer].editLock?.mode === activeEditMode)
    ) {
      return;
    }

    const value = togglePlotMapLayer(prefs, layer).join(",") || null;

    setSearchParams(
      (prev) => updateParamValue(prev, LAYERS_PREF_KEY, prefs, value),
      { replace: true, state: { inPageNav: true } },
    );
  };

  const handleDisplayOrtho = (orthoId: number) => {
    displayOrtho(orthoId);
  };

  const handleZoomToOrtho = (orthoId: number) => {
    const bounds = sessions[orthoId]?.overlay?.bounds;
    if (bounds == null || map == null) {
      return;
    }

    map.fitBounds(bounds, FIT_BOUNDS_OPTIONS);
  };

  return (
    <>
      <Tooltip title="Layers">
        <MapControlIconButton
          aria-controls={isOpen ? menuId : undefined}
          aria-expanded={isOpen ? "true" : undefined}
          aria-haspopup="menu"
          aria-label="Layers"
          disabled={!isReady}
          onClick={handleOpen}
        >
          <Layers />
        </MapControlIconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "left", vertical: "top" }}
        id={menuId}
        onClose={handleClose}
        open={isOpen}
        slotProps={{
          paper: {
            sx: { mt: -1.5 },
          },
        }}
        transformOrigin={{ horizontal: "left", vertical: "bottom" }}
      >
        <ListSubheader
          component="div"
          disableSticky
          sx={{ fontWeight: "bold", lineHeight: 2.5 }}
        >
          LAYERS
        </ListSubheader>
        {PLOT_MAP_LAYER_ORDER.map((layer) => {
          const { label } = PLOT_MAP_LAYERS[layer];
          const isVisible = isPlotMapLayerVisible(prefs, layer);
          const editLock = PLOT_MAP_LAYERS[layer].editLock;
          const lockTooltip =
            activeEditMode != null && editLock?.mode === activeEditMode
              ? editLock.tooltip
              : null;
          return (
            <Fragment key={layer}>
              <MenuItem
                aria-label={
                  lockTooltip != null
                    ? label
                    : `${isVisible ? "Hide" : "Show"} ${label}`
                }
                disabled={!isReady || lockTooltip != null}
                onClick={() => {
                  handleToggleLayer(layer);
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <LayerSwatch layer={layer} />
                </ListItemIcon>
                <ListItemText primary={label} />
                <ListItemIcon
                  sx={
                    lockTooltip != null
                      ? {
                          alignItems: "center",
                          gap: 0.5,
                          justifyContent: "flex-end",
                          minWidth: 0,
                          ml: 2,
                        }
                      : { justifyContent: "flex-end", minWidth: 0, ml: 2 }
                  }
                >
                  {lockTooltip != null && (
                    <Tooltip title={lockTooltip}>
                      <IconButton
                        aria-label={lockTooltip}
                        disableRipple
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                        size="small"
                        sx={{ cursor: "default", pointerEvents: "auto" }}
                      >
                        <Info color="info" fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {lockTooltip != null ? (
                    <Lock aria-hidden fontSize="small" />
                  ) : isVisible ? (
                    <Visibility aria-hidden fontSize="small" />
                  ) : (
                    <VisibilityOff aria-hidden fontSize="small" />
                  )}
                </ListItemIcon>
              </MenuItem>
              {nestedLabelLayers
                .filter((nested) => nested.parent === layer)
                .map((nested) => (
                  <MenuItem
                    key={nested.layer}
                    aria-label={`${nested.visible ? "Hide" : "Show"} ${nested.label}`}
                    disabled={!isReady}
                    onClick={() => {
                      handleToggleLayer(nested.layer);
                    }}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <LayerSwatch layer={nested.layer} />
                    </ListItemIcon>
                    <ListItemText primary={nested.label} />
                    <ListItemIcon
                      sx={{
                        alignItems: "center",
                        gap: 0.5,
                        justifyContent: "flex-end",
                        minWidth: 0,
                        ml: 2,
                      }}
                    >
                      {nested.belowMinZoom && nested.zoomTooltip != null && (
                        <Tooltip title={nested.zoomTooltip}>
                          <IconButton
                            aria-label={nested.zoomTooltip}
                            disableRipple
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                            size="small"
                            sx={{ cursor: "default" }}
                          >
                            <Info color="info" fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {nested.visible ? (
                        <Visibility aria-hidden fontSize="small" />
                      ) : (
                        <VisibilityOff aria-hidden fontSize="small" />
                      )}
                    </ListItemIcon>
                  </MenuItem>
                ))}
              {"orth" === layer &&
                orthoSiteGroups.map((group) => (
                  <Fragment key={group.siteId}>
                    <ListSubheader
                      component="div"
                      disableSticky
                      sx={{ pl: 4, lineHeight: 2.5 }}
                    >
                      {group.siteLabel}
                    </ListSubheader>
                    {group.orthos.map((ortho) => {
                      const orthoLabel = formatOrthoLayerLabel(
                        ortho,
                        mapVisibleOrthos,
                      );
                      const isOrthoVisible = ortho.isVisible;
                      const session = sessions[ortho.id];
                      const hasBounds = session?.overlay?.bounds != null;
                      const canZoomToOrtho = map != null && hasBounds;
                      const hasOrthoLoadError = session?.error != null;
                      const isOrthoLoading =
                        isVisible &&
                        isOrthoVisible &&
                        !hasBounds &&
                        !hasOrthoLoadError;
                      const showZoomToOrtho =
                        !isOrthoLoading && !hasOrthoLoadError && isOrthoVisible;

                      return (
                        <MenuItem
                          key={ortho.id}
                          aria-busy={isOrthoLoading}
                          aria-label={orthoLabel}
                          disabled={!isReady}
                          onClick={() => {
                            handleDisplayOrtho(ortho.id);
                          }}
                          sx={{
                            bgcolor: isOrthoVisible
                              ? SELECTED_MAP_ITEM_BGCOLOR
                              : undefined,
                            pl: 6,
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <LayerSwatch layer="orth" />
                          </ListItemIcon>
                          <ListItemText primary={orthoLabel} />
                          <ListItemIcon
                            sx={{
                              alignItems: "center",
                              gap: 0.5,
                              justifyContent: "flex-end",
                              minWidth: 0,
                              ml: 2,
                            }}
                          >
                            {isOrthoLoading && (
                              <IconButton
                                aria-label={`Loading ${orthoLabel}`}
                                disableRipple
                                size="small"
                                sx={{
                                  cursor: "default",
                                  pointerEvents: "none",
                                }}
                              >
                                <CircularProgress color="primary" size={20} />
                              </IconButton>
                            )}
                            {hasOrthoLoadError && (
                              <Tooltip title="Failed to load ortho">
                                <IconButton
                                  aria-label={`Failed to load ${orthoLabel}`}
                                  disableRipple
                                  size="small"
                                  sx={{ cursor: "default" }}
                                >
                                  <ErrorOutlined
                                    color="error"
                                    fontSize="small"
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                            {showZoomToOrtho && (
                              <Tooltip title="Zoom to ortho">
                                <span>
                                  <IconButton
                                    aria-label={`Zoom to ${orthoLabel}`}
                                    disabled={!canZoomToOrtho}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleZoomToOrtho(ortho.id);
                                    }}
                                    size="small"
                                  >
                                    <FilterCenterFocus fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </ListItemIcon>
                        </MenuItem>
                      );
                    })}
                  </Fragment>
                ))}
            </Fragment>
          );
        })}
      </Menu>
    </>
  );
};

export default LayersControl;
